import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SchemaOrg from "@/components/feature/SchemaOrg";

interface ArticleLayoutProps {
  breadcrumbTitle: string;
  badgeLabel: string;
  h1: React.ReactNode;
  description: string;
  updatedDate: string;
  children: React.ReactNode;
  relatedLinks: Array<{ label: string; to: string }>;
}

const faqData = [
  {
    q: "Do I need an account to use a CSSBuy Spreadsheet?",
    a: "No account or registration is required. All CSSBuy Spreadsheet resources are freely accessible — simply browse the categories and follow the product links that interest you.",
  },
  {
    q: "How do I know the spreadsheet information is trustworthy?",
    a: "Every listing in our recommended CSSBuy Spreadsheet collections is verified by real community members who have personally ordered and reviewed the items. We prioritize spreadsheets with active maintainers and recent update timestamps.",
  },
  {
    q: "How often are CSSBuy Spreadsheets updated?",
    a: "The best CSSBuy Spreadsheet collections are updated weekly. Our recommended sheets include clear last-updated timestamps so you can gauge the freshness of the information before browsing.",
  },
];

export default function ArticleLayout({
  breadcrumbTitle,
  badgeLabel,
  h1,
  description,
  updatedDate,
  children,
  relatedLinks,
}: ArticleLayoutProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL;

  return (
    <>
      <Navbar />
      <main>
        <article>
          {/* Breadcrumb */}
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                <Link to="/" className="hover:text-primary-500 transition-colors duration-200 whitespace-nowrap">
                  Home
                </Link>
                <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
                <span className="text-foreground-700 font-medium">{breadcrumbTitle}</span>
              </nav>
            </div>
          </div>

          {/* Article Header */}
          <header className="bg-background-50 py-12 md:py-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-label font-semibold rounded-full">
                  {badgeLabel}
                </span>
                <span className="text-xs text-foreground-500">Updated {updatedDate}</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                {h1}
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {description}
              </p>
            </div>
          </header>

          {/* Article Content */}
          <div className="bg-background-50 py-10 md:py-14">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="prose-custom space-y-8">
                {children}
              </div>
            </div>
          </div>
        </article>

        {/* Related Guides */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-5 text-center">
              Explore More <strong className="text-primary-600">CSSBuy Spreadsheet</strong> Guides
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {relatedLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-4 hover:border-primary-300 transition-all duration-200 group cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-primary-500 group-hover:translate-x-1 transition-transform duration-200"></i>
                    <span className="text-sm text-foreground-700 group-hover:text-foreground-900 font-medium">
                      {link.label}
                    </span>
                  </div>
                </Link>
              ))}
              <Link
                to="/"
                className="bg-background-50 rounded-lg border border-background-200/70 p-4 hover:border-primary-300 transition-all duration-200 group cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <i className="ri-home-4-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                  <span className="text-sm text-foreground-700 group-hover:text-foreground-900 font-medium">
                    Back to Homepage
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6 text-center">
              Quick <strong className="text-primary-600">CSSBuy Spreadsheet</strong> FAQ
            </h2>
            <SchemaOrg
              data={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqData.map((item) => ({
                  "@type": "Question",
                  "name": item.q,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": item.a,
                  },
                })),
              }}
            />

            <div className="space-y-3">
              {faqData.map((item, i) => (
                <div key={i} className="bg-background-100 rounded-lg border border-background-200/70 p-5">
                  <h4 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    {item.q}
                  </h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}